import React from 'react';
import { TruckIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label, LabelStacked } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import FlagInput, { type FlagValue } from '../../pro/components/orderform/FlagInput';
import Stack from '../../pro/components/orderform/Stack';
import Icon from '../../pro/components/orderform/Icon';
import Card from '../components/Card';
import FieldValue from '../components/FieldValue';
import useQuoteFsc, { fscRateUnit } from '../components/useQuoteFsc';
import AutoCalcRow from '../components/AutoCalcRow';
import TariffRatesCard from '../components/TariffRatesCard';
import CustTariffValue from '../components/CustTariffValue';
import FuelRateSettingsRows from '../components/FuelRateSettingsRows';
import type { WarehouseFields, TariffRateRow } from '../fixtures';
import type { ChargeCatalogRow, FscTariff, RateStandards } from '../../../services/quote.service';
import { drayGuard, belowMinimum } from '../rateGuards';
import { MOVE_TYPES, WHSE_TYPES, RATE_ZONES, PACKAGE_TYPES, NUM_UNITS, SIZES, EQUIP_TYPES, WAREHOUSES, FUEL_TYPES_WAREHOUSE, withCurrent } from '../quoteOptions';
import { triaxleReadOnly, swingReadOnly, hasCharge } from '../quoteFieldRules';
import { quoteOverweight } from '../quoteOverweight';

const LBL = 100;

interface Props {
  createMode?: boolean;
  data: WarehouseFields;
  tariffRates?: TariffRateRow[];
  /** The quote's bill-to recno — enables the FSC "Cust Tariff #" Lookup. */
  billTo?: number;
  /** Drawer-only per-quote Fuel Calculation / Rate Applies (masterquote
   *  fields). They govern the FSC above them, so they render here rather than
   *  in a separate card floating above the quote body. */
  fuelCalc?: string;
  rateApplies?: string;
  onFuelRateChange?: (field: 'fuelCalc' | 'rateApplies', value: string) => void;
  /**
   * Secured-quote exemption: assign a still-broken customer FSC tariff even
   * though the tab is otherwise read-only (no `onWarehouseChange`). Mirrors the
   * Direct tab, and the same host state drives both.
   */
  onAssignTariff?: (t: FscTariff) => void;
  /**
   * The SAVED customer FSC tariff number when that tariff has been DELETED from
   * fueldata.fsc_tariff (backend `fscTariffDeleted`).
   */
  deletedTariffNumber?: number | null;
  /** Optional change callback (same contract as DirectTab.onDirectChange):
   *  when provided, inputs become controlled-editable and emit
   *  `(field, value)` with the camelCase WarehouseFields key. When ABSENT,
   *  every input renders read-only display exactly as before. */
  onWarehouseChange?: (field: keyof WarehouseFields, value: string) => void;
  /** Charge (tariff-rate) CRUD pass-throughs for TariffRatesCard. */
  chargesEditable?: boolean;
  chargeCatalog?: ChargeCatalogRow[];
  onAddCharge?: (p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number }) => void;
  onChangeCharge?: (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number }) => void;
  onDeleteCharge?: (id: number) => void;
  /** Live rate-zone codes (cms.ratezones); falls back to static RATE_ZONES. */
  rateZoneOptions?: string[];
  /** Standards lookups for the below-minimum dray-rate warning. */
  rateStandards?: RateStandards | null;
}

/**
 * Dray tab for WAREHOUSE quotes. Reads from data.warehouse (transloadquote).
 * For DIRECT quotes, see DirectTab.
 */
const DrayTab: React.FC<Props> = ({
  data, tariffRates = [], onWarehouseChange, createMode = false,
  billTo, onAssignTariff, deletedTariffNumber,
  fuelCalc, rateApplies, onFuelRateChange,
  chargesEditable, chargeCatalog, onAddCharge, onChangeCharge, onDeleteCharge,
  rateZoneOptions, rateStandards = null,
}) => {
  const ch = (field: keyof WarehouseFields) =>
    onWarehouseChange ? (v: string) => onWarehouseChange(field, v) : undefined;
  // Live rate-zone list when present, else the static fallback.
  const rateZones = rateZoneOptions && rateZoneOptions.length ? rateZoneOptions : RATE_ZONES;

  // Below-minimum dray-rate guard (V1 checkfield_drayage + draystandards).
  const guard = drayGuard(rateStandards, data.warehouse, data.rateZone);
  const drayBelowMin = belowMinimum(guard, data.drayRate);

  // N20: overweight highlight against the quote matrix (WAREHOUSE quote type).
  const ow = quoteOverweight(data, 'WAREHOUSE');
  // N21 (defs_quote.pl:271,308-321): a STORAGE account suppresses dray entry.
  // N13: reveal the yard free-time sub-input on its charge (or existing value).
  const showYard = hasCharge(tariffRates, 'YARD STORAGE') || !!data.yardStorageFree;

  // FSC resolution, the same hook the Direct tab uses. A warehouse lane has no
  // delivery zone and no miles, which is fine for the two auto-filled types:
  // CURRENT reads the OTR fuel % regardless of zone (V1 DS2-3171) and CUSTOMER
  // reads its tariff. Miles only move the estimated-revenue adjustment, not the
  // rate that lands in the field.
  const fsc = useQuoteFsc({
    fsctype: data.fuelType ?? '',
    fsctariff: data.fuelTariff != null ? String(data.fuelTariff) : '',
    deliveryzone: '',
    fscrate: data.fuelSurcharge ?? '',
    dray: data.drayWithCharges ?? '',
    miles: '',
    enabled: !!onWarehouseChange,
    resolveReadOnly: true,
    onFscRate: (r) => onWarehouseChange?.('fuelSurcharge', r),
  });
  // Only the user-entered types (PCT% / MILES / DISCOUNTED) get an input box,
  // exactly as on Direct -- FLAT included, even though it is most of the
  // warehouse book.
  const fscUnit = fscRateUnit(data.fuelType ?? '', fsc.ratetype);
  // The auto-filled types are dynamic (CURRENT moves weekly, a CUSTOMER tariff
  // drifts), so show the live resolved rate rather than the stored snapshot.
  // Everything else falls back to what is stored.
  const displayFscRate = fsc.resolvedRate != null ? fsc.resolvedRate : data.fuelSurcharge;

  return (
    <>
      <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-3">
        <Card icon={<TruckIcon className="h-3.5 w-3.5" />} title="Move Details">
          <Row><Label width={LBL}>Move Type</Label><SelectInput ariaLabel="Warehouse Move Type" highlight={createMode && !data.moveType ? 'red' : undefined} value={data.moveType} onChange={ch('moveType')} options={withCurrent(data.moveType, MOVE_TYPES)} disabled={!onWarehouseChange} className="min-w-[10rem]" /></Row>
          <Row><Label width={LBL}>Whse Type</Label><SelectInput ariaLabel="Whse Type" highlight={createMode && !data.whseType ? 'red' : undefined} value={data.whseType} onChange={ch('whseType')} options={withCurrent(data.whseType, WHSE_TYPES)} disabled={!onWarehouseChange} className="min-w-[13rem]" /></Row>
          <Row><Label width={LBL}>Rate Zone</Label><SelectInput ariaLabel="Warehouse Rate Zone" highlight={createMode && !data.rateZone ? 'red' : undefined} value={data.rateZone} onChange={ch('rateZone')} options={withCurrent(data.rateZone, rateZones)} disabled={!onWarehouseChange} className="min-w-[13rem]" /></Row>
          <Row><Label width={LBL}>Warehouse</Label><SelectInput ariaLabel="Warehouse" highlight={createMode && !data.warehouse ? 'red' : undefined} value={data.warehouse} onChange={ch('warehouse')} options={withCurrent(data.warehouse, WAREHOUSES)} disabled={!onWarehouseChange} className="min-w-[13rem]" /></Row>

          <Divider />

          <Row><Label width={LBL}>Commodity</Label><TextInput ariaLabel="Commodity" value={data.commodity} onChange={ch('commodity')} highlight={createMode && !data.commodity ? 'red' : undefined} readOnly={!onWarehouseChange} /></Row>
          <Row><Label width={LBL}>Package Type</Label><SelectInput ariaLabel="Package Type" highlight={createMode && !data.packageType ? 'red' : undefined} value={data.packageType} onChange={ch('packageType')} options={withCurrent(data.packageType, PACKAGE_TYPES)} disabled={!onWarehouseChange} className="min-w-[13rem]" /></Row>
          <Row><Label width={LBL}># of Units</Label><SelectInput value={data.numUnits} onChange={ch('numUnits')} options={withCurrent(data.numUnits, ['', ...NUM_UNITS])} width={96} placeholder="—" disabled={!onWarehouseChange} /></Row>
          <Row>
            {/* The only label in this tab longer than the shared LBL box. `Label` is
                `whitespace-nowrap`, so it was spilling over the input beside it. WRAPPED
                rather than widened: LBL is shared by 19 rows across three cards, and
                growing it to fit one label would shift every input on the tab to keep
                this row's text from touching its own. `!` because the override competes
                with a same-specificity utility, where source order decides and class
                order does not. */}
            <Label width={LBL} className="!whitespace-normal leading-tight">
              # of Included Overflow
            </Label>
            <TextInput value={String(data.numOverflow ?? 0)} onChange={ch('numOverflow')} width={64} readOnly={!onWarehouseChange} />
            {/* Sized to the word. `Label` defaults to 88px, which put a right-aligned
                three-letter word at the far end of its own box and opened a gap wide
                enough to read as a missing field. */}
            <Label width={28}>Per</Label>
            <TextInput value={String(data.perContainers ?? 0)} onChange={ch('perContainers')} width={48} readOnly={!onWarehouseChange} />
            <span className="text-[11px] text-slate-500 dark:text-slate-400">Containers</span>
          </Row>
          <Row>
            <Label width={LBL}>Size / Type</Label>
            <SelectInput ariaLabel="Warehouse Size" highlight={createMode && !data.size ? 'red' : undefined} value={data.size} onChange={ch('size')} options={withCurrent(data.size, SIZES)} width={80} disabled={!onWarehouseChange} />
            <SelectInput value={data.equipType} onChange={ch('equipType')} options={withCurrent(data.equipType, EQUIP_TYPES)} disabled={!onWarehouseChange} />
          </Row>
          <Row>
            <Label width={LBL}>Lbs / Kgs</Label>
            <TextInput value={data.lbs} onChange={ch('lbs')} highlight={ow.lbsOver ? 'red' : undefined} ariaLabel="Lbs" width={96} readOnly={!onWarehouseChange} />
            <TextInput value={data.kgs} onChange={ch('kgs')} highlight={ow.kgsOver ? 'red' : undefined} ariaLabel="Kgs" width={96} readOnly={!onWarehouseChange} />
            <label className="inline-flex items-center gap-1 text-[11.5px] text-slate-600 dark:text-slate-300 cursor-pointer ml-1">
              <input
                type="checkbox"
                checked={data.legalWeight === 'Y'}
                disabled={!onWarehouseChange}
                onChange={onWarehouseChange ? (e) => onWarehouseChange('legalWeight', e.target.checked ? 'Y' : 'N') : undefined}
              /> Legal
            </label>
          </Row>

          <Divider />

          <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">Flags</div>
          <div className="grid grid-cols-2 gap-x-1.5">
            <FlagInput label="Overweight" value={data.overweight as FlagValue} onChange={ch('overweight')} labelWidth={88} readOnly={!onWarehouseChange} />
            <FlagInput label="Triaxle"    value={data.triaxle as FlagValue}    onChange={ch('triaxle')}    labelWidth={64} readOnly={!onWarehouseChange || triaxleReadOnly(data, false)} />
            <FlagInput label="Swing"      value={data.swing as FlagValue}      onChange={ch('swing')}      labelWidth={88} readOnly={!onWarehouseChange || swingReadOnly(data)} />
            <FlagInput label="Hazmat"     value={data.hazmat as FlagValue}     onChange={ch('hazmat')}     labelWidth={64} readOnly={!onWarehouseChange} />
          </div>

        </Card>

        <Card icon={<Icon.ship className="h-3.5 w-3.5" />} title="Charges">
          <Row>
            <Label width={LBL}>Drayage</Label>
            <TextInput value={data.drayRate} onChange={ch('drayRate')} readOnly={!onWarehouseChange} ariaLabel={createMode ? "Warehouse Dray Rate" : "Drayage Rate"} width={120} />
            {guard && (
              <span className={`text-[10.5px] whitespace-nowrap ${drayBelowMin ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-400 dark:text-slate-500'}`}>
                {drayBelowMin ? `below min $${guard.minimumRate}` : `std $${guard.standardRate} / min $${guard.minimumRate}`}
              </span>
            )}
          </Row>

          <AutoCalcRow label="Total With Charges" value={data.drayWithCharges} labelWidth={LBL} />

          {/* Fuel Type above FSC, matching the Direct tab. Switching the type
              clears the rate; the hook re-fills CURRENT / CUSTOMER. */}
          <Row>
            <Label width={LBL}>Fuel Type</Label>
            <SelectInput ariaLabel="Warehouse Fuel Type" highlight={createMode && !data.fuelType ? 'red' : undefined} value={data.fuelType} onChange={(v) => { onWarehouseChange?.('fuelType', v); onWarehouseChange?.('fuelSurcharge', ''); }} options={withCurrent(data.fuelType, FUEL_TYPES_WAREHOUSE)} disabled={!onWarehouseChange} width={130} />
          </Row>
          <Row>
            <Label width={LBL}>FSC</Label>
            {/* Calculated rate (CURRENT / CUSTOMER) shows as text; the
                user-entered types keep an input box. */}
            {fsc.fscReadOnly
              ? <FieldValue value={displayFscRate || '—'} width={96} />
              : <TextInput value={data.fuelSurcharge} onChange={ch('fuelSurcharge')} readOnly={!onWarehouseChange} ariaLabel={createMode ? "Warehouse Fuel" : "FSC Rate"} placeholder="—" width={96} />}
            {fscUnit && <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400 w-14">{fscUnit}</span>}
          </Row>
          {data.fuelType === 'CUSTOMER' && (
            <Row>
              <Label width={LBL}>Cust Tariff #</Label>
              {/* Was a bare number input: no lookup, and nothing said when the
                  lane pointed at a missing or deleted tariff. Same control the
                  Direct tab uses, writing the warehouse FSC columns. */}
              <CustTariffValue
                tariffNumber={data.fuelTariff}
                billTo={billTo}
                deletedTariffNumber={deletedTariffNumber}
                editable={!!onWarehouseChange}
                onAssignTariff={onAssignTariff}
                onPick={(t) => {
                  onWarehouseChange?.('fuelType', 'CUSTOMER');
                  onWarehouseChange?.('fuelTariff', String(t.tariffNumber));
                  onWarehouseChange?.('fuelSurcharge', t.currentRate != null ? String(t.currentRate) : '');
                }}
              />
            </Row>
          )}
          <FuelRateSettingsRows fuelCalc={fuelCalc} rateApplies={rateApplies}
                                onFuelRateChange={onFuelRateChange} labelWidth={LBL} />

          <Divider />

          {/* Chassis Fee / LW Chassis Fee / Waiting Free are DIRECT-only
              (see DirectTab) — a WAREHOUSE quote never shows them, even
              though transloadquote carries FLAT / 1.0 column defaults. */}
          {showYard && (
          <Row>
            <Label width={LBL}>Yard Free (days)</Label>
            <TextInput value={data.yardStorageFree != null ? String(data.yardStorageFree) : ''} onChange={ch('yardStorageFree')} ariaLabel="Yard Free Days" width={56} readOnly={!onWarehouseChange} />
          </Row>
          )}
        </Card>

        <Card icon={<ChatBubbleLeftIcon className="h-3.5 w-3.5" />} title="Comments & Cargo">
          <Stack>
            <LabelStacked>Comments</LabelStacked>
            <textarea
              value={data.comments}
              onChange={onWarehouseChange ? (e) => onWarehouseChange('comments', e.target.value) : undefined}
              readOnly={!onWarehouseChange}
              placeholder="Notes for this dray…"
              className="w-full min-h-[100px] border border-slate-300 rounded px-2 py-1.5 text-[12px] text-slate-900 resize-y bg-white font-sans dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </Stack>

        </Card>
      </div>

      {/* Tariff Rates scoped to the dray side of the quote
          (quoteadditionalcharges.tablename = 'transloadquote'). */}
      <TariffRatesCard
        rows={tariffRates}
        caption="dray-scoped charges"
        editable={chargesEditable}
        catalog={chargeCatalog}
        onAdd={onAddCharge}
        onChangeRow={onChangeCharge}
        onDeleteRow={onDeleteCharge}
      />
    </>
  );
};

const Divider: React.FC = () => <div className="h-px bg-slate-100 dark:bg-slate-700 my-2" />;

export default DrayTab;
